import clsx from 'clsx';
import { useEffect } from 'react';
import { useLocalStorageState } from 'ahooks';
import { Link, useLocation, type LinkProps } from 'react-router-dom';

import { useRouteInfo } from '@/hooks/use-route-info';

import { Icon } from '@flintmint/web-shared/components/icon';

import { appRoutes } from '@/constants/routes';
import { DROPPED_FROM_BY_ROUTE_KEY } from '@/constants/navigation';

import styles from './back-to.module.scss';

const BACK_TO_FROM_BY_ROUTE_KEY = 'backToFromByRoute';

type BackToLocationState = {
  backToFrom?: string;
};

type BackToDestination = {
  route: string;
  label: string;
};

export type BackToProps = Omit<LinkProps, 'children' | 'to'>;

export const BackTo = (props: BackToProps) => {
  const { className, ...rest } = props;
  const location = useLocation();
  const { basePath } = useRouteInfo();
  const fromState = (location.state as BackToLocationState | null)?.backToFrom;

  const [droppedFromMap = {}] = useLocalStorageState<Record<string, string>>(DROPPED_FROM_BY_ROUTE_KEY, {
    defaultValue: {},
  });

  useEffect(() => {
    if (!fromState) return;
    writeBackToFromForRoute(basePath, fromState);
  }, [basePath, fromState]);

  const storedFrom = readBackToFromForRoute(basePath);
  const { route, label } = resolveBackToDestination(fromState ?? storedFrom, basePath);
  const to = droppedFromMap[route] ?? route;

  return (
    <Link to={to} data-testid="back-to" aria-label={`Back to ${label}`} className={clsx(styles.backTo, className)} {...rest}>
      <Icon type="arrow-left-02-solid" size={16} className={styles.icon} />
      <span>Back to {label}</span>
    </Link>
  );
};

const DEFAULT_BACK_TO_DESTINATION: BackToDestination = {
  route: appRoutes.library,
  label: 'Editor',
};

const BACK_TO_LABELS: Record<string, string> = {
  [appRoutes.home]: 'Editor',
  [appRoutes.library]: 'Editor',
  [appRoutes.cortex]: 'Cortex',
  [appRoutes.tags]: 'Tags',
  [appRoutes.instructions]: 'Instructions',
  [appRoutes.archive]: 'Archive',
};

export const resolveBackToDestination = (enteredFrom?: string | null, currentBasePath?: string): BackToDestination => {
  if (enteredFrom && enteredFrom !== currentBasePath && BACK_TO_LABELS[enteredFrom]) {
    return { route: enteredFrom, label: BACK_TO_LABELS[enteredFrom] };
  }

  return DEFAULT_BACK_TO_DESTINATION;
};

const readBackToFromMap = (): Record<string, string> => {
  try {
    const raw = sessionStorage.getItem(BACK_TO_FROM_BY_ROUTE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const readBackToFromForRoute = (basePath: string) => readBackToFromMap()[basePath] ?? null;

const writeBackToFromForRoute = (basePath: string, from: string) => {
  sessionStorage.setItem(BACK_TO_FROM_BY_ROUTE_KEY, JSON.stringify({ ...readBackToFromMap(), [basePath]: from }));
};
